import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Container, Section, Heading, LinkButton } from "./ui";

export interface Faq {
  q: string;
  a: string;
}

/** Build FAQPage JSON-LD from the SAME items the page renders, so the
 *  structured data can never drift from the visible content (a Google
 *  rich-results requirement). Pass the result to <Seo jsonLd={...} />. */
export function faqPageJsonLd(items: Faq[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** Visible FAQ — native <details> accordion (accessible, no JS). */
export function FaqSection({ items, heading = "Frequently asked questions" }: { items: Faq[]; heading?: string }) {
  return (
    <Section tone="soft">
      <Container>
        <Heading level={2}>{heading}</Heading>
        <div className="mt-8 max-w-3xl divide-y divide-border rounded-2xl border border-border bg-surface">
          {items.map((f) => (
            <details key={f.q} className="group px-6 py-5 [&_summary]:cursor-pointer">
              <summary className="flex items-center justify-between gap-4 text-base font-semibold text-ink-900 marker:content-['']">
                {f.q}
                <span className="text-forest-600 transition-transform group-open:rotate-45" aria-hidden>
                  +
                </span>
              </summary>
              <p className="mt-3 text-[0.9375rem] leading-relaxed text-ink-600">{f.a}</p>
            </details>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/** Contextual internal links (SEO: spreads link equity to the money pages). */
export function RelatedLinks({
  heading = "Related",
  items,
}: {
  heading?: string;
  items: { to: string; label: string; desc?: string }[];
}) {
  return (
    <Section>
      <Container>
        <Heading level={2}>{heading}</Heading>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              className="group rounded-2xl border border-border bg-surface p-5 transition-colors hover:border-forest-700"
            >
              <span className="flex items-center justify-between gap-2 font-semibold text-ink-900 group-hover:text-forest-700">
                {it.label}
                <ArrowRight className="h-4 w-4 flex-none text-forest-700" aria-hidden />
              </span>
              {it.desc ? <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{it.desc}</p> : null}
            </Link>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/** Dark conversion band used at the foot of the marketing landing pages. */
export function CtaBand({
  title,
  body,
  primary = { to: "/demo", label: "Book a demo" },
  secondary,
}: {
  title: ReactNode;
  body: ReactNode;
  primary?: { to: string; label: string };
  secondary?: { to: string; label: string };
}) {
  return (
    <Section>
      <Container>
        <div className="rounded-3xl bg-forest-gradient px-8 py-14 text-center text-white sm:px-14">
          <Heading level={2} className="text-white">
            {title}
          </Heading>
          <p className="mx-auto mt-4 max-w-2xl text-white/85">{body}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <LinkButton to={primary.to} variant="secondary">
              {primary.label}
            </LinkButton>
            {secondary ? (
              <LinkButton to={secondary.to} variant="ghost" className="text-white hover:bg-white/10">
                {secondary.label}
              </LinkButton>
            ) : null}
          </div>
        </div>
      </Container>
    </Section>
  );
}
